/**
 * UiModeToggle
 *
 * Header pill that flips the UI between two surfaces:
 *   - Technical: shows validator findings, quarantined rules, extraction
 *     provenance, rule IDs, run IDs. The pitch-day / review surface.
 *   - End-user: shows the cited code paragraph, the measured-vs-required
 *     comparison, the indeterminate-exceptions explanation. The
 *     customer-facing surface.
 *
 * The store is persisted to localStorage so a reload mid-presentation
 * doesn't kick the user back to technical.
 */
import { type UiMode, useUiModeStore } from "../stores/uiModeStore";

export function UiModeToggle() {
  const mode = useUiModeStore(s => s.mode);
  const setMode = useUiModeStore(s => s.setMode);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 12,
      }}
    >
      <span
        style={{
          color: "var(--fc2-text-dim)",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontSize: 10,
        }}
      >
        View
      </span>
      <div
        style={{
          display: "inline-flex",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid var(--fc2-border)",
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        <Pill
          active={mode === "end-user"}
          onClick={() => setMode("end-user")}
          label="End user"
          tooltip="What an architect sees: cited code, measured-vs-required, what FC2 could not decide. Hides validator internals."
        />
        <Pill
          active={mode === "technical"}
          onClick={() => setMode("technical")}
          label="Technical"
          tooltip="Full surface: validator findings, quarantined rules, extraction provenance, rule IDs. The pitch-day view."
        />
      </div>
    </div>
  );
}

function Pill({
  active,
  onClick,
  label,
  tooltip,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  tooltip: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={tooltip}
      style={{
        background: active ? "rgba(127,179,213,0.18)" : "transparent",
        color: active ? "#7fb3d5" : "var(--fc2-text)",
        border: "none",
        padding: "4px 10px",
        cursor: "pointer",
        font: "inherit",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        fontWeight: active ? 600 : 400,
      }}
    >
      {label}
    </button>
  );
}

/** Type re-export so consumers don't import from the store directly. */
export type { UiMode };
